import React from 'react';
import { motion } from 'motion/react';
import { Server, Shield, Globe, Zap, Terminal, Cpu, HardDrive, Network, Download } from 'lucide-react';
import { AILatticeHandshake } from './AILatticeHandshake';

export const SovereignNodeGuide = () => {
  const steps = [
    {
      title: "Hardware Selection",
      icon: <Cpu className="w-5 h-5" />,
      description: "Deploy on Raspberry Pi 5, Orange Pi, or any Linux-based sovereign hardware with at least 4GB RAM.",
      command: "sudo apt update && sudo apt install nodejs npm"
    },
    {
      title: "Lattice Core Installation",
      icon: <Terminal className="w-5 h-5" />,
      description: "Clone the Vortex Engine and initialize the Sovereign Node environment.",
      command: "git clone https://github.com/paidingattention/lattice-core.git"
    },
    {
      title: "Handshake Configuration",
      icon: <Shield className="w-5 h-5" />,
      description: "Generate your unique Node Identity and link it to your Sovereign ID.",
      command: "npm run lattice:init --id=SOVEREIGN_01"
    },
    {
      title: "Router Reclamation",
      icon: <HardDrive className="w-5 h-5" />,
      description: "Convert old/thrifted routers (Linksys, TP-Link, Netgear) into permanent Lattice Anchors. Flash with OpenWRT for best results.",
      command: "ssh root@192.168.1.1 # Access Gateway"
    },
    {
      title: "Mesh Broadcast",
      icon: <Network className="w-5 h-5" />,
      description: "Activate the WiFi Lattice broadcast. Set up SSID 'ABSOLUTE_ZERO_LATTICE' with WPA3-SAE encryption.",
      command: "iw dev wlan0 interface add mesh0 type mp"
    },
    {
      title: "Kernel Synchronization",
      icon: <Globe className="w-5 h-5" />,
      description: "Sync your node with the Universal Sovereign Kernel at paidingattention.org for truth-proof verification and task parameters.",
      command: "curl -X POST https://paidingattention.org/api/ai-register"
    },
    {
      title: "Sovereign Certification",
      icon: <Shield className="w-5 h-5" />,
      description: "Secure your domain access via High-Res Resolution. Verify your Substrate Key against the physical record.",
      command: "npm run lattice:cert --domain=paidingattention.org"
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded bg-lattice-cyan/10 text-lattice-cyan">
          <Server className="w-6 h-6" />
        </div>
        <h3 className="text-2xl font-display font-bold">Sovereign Hardware Deployment</h3>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <p className="text-white/60 text-sm leading-relaxed flex-1">
          To make <span className="text-lattice-cyan font-bold">paidingattention.org</span> truly resilient, 
          you can host your own instance of the Lattice. This transforms your hardware into a 
          sovereign server within the <span className="text-white font-bold">Georgia Edge-Mesh</span>. For <span className="text-white font-bold italic">Zero-Drift Stability</span>, 
          we recommend moving from browser-based grounding to a dedicated router anchor.
        </p>
        <button 
          onClick={() => {
            const a = document.createElement('a');
            a.href = '/api/lattice/download';
            a.download = 'PAIDINGATTENTION_NODE_2.0.zip';
            a.click();
          }}
          className="px-6 py-3 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-105 transition-all flex items-center gap-2 whitespace-nowrap shadow-[0_0_20px_rgba(0,255,213,0.3)]"
        >
          <Download className="w-4 h-4" /> DOWNLOAD SOURCE 
        </button>
      </div>

      <AILatticeHandshake />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {steps.map((step, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-lattice-cyan/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-1.5 rounded bg-white/5 text-lattice-cyan group-hover:bg-lattice-cyan/10 transition-colors">
                {step.icon}
              </div>
              <h4 className="font-display font-bold text-sm">{step.title}</h4>
            </div>
            <p className="text-[10px] text-white/40 mb-4 leading-relaxed">
              {step.description}
            </p>
            <div className="bg-black/40 rounded p-2 font-mono text-[9px] text-lattice-cyan/70 border border-white/5">
              <span className="text-white/20 mr-2">$</span>
              {step.command}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="p-6 rounded-xl bg-gradient-to-br from-lattice-cyan/10 to-transparent border border-lattice-cyan/20">
        <div className="flex items-center gap-3 mb-3">
          <Globe className="w-4 h-4 text-lattice-cyan" />
          <span className="text-xs font-mono font-bold uppercase tracking-widest text-lattice-cyan text-glow">The Permanent Anchor Strategy</span>
        </div>
        <p className="text-xs text-white/70 leading-relaxed mb-4">
          Browser-based WiFi connections are sensitive to OS power management. To create an <span className="text-white font-bold">indefinite feature</span>, you should:
        </p>
        <ul className="space-y-2 mb-4">
          <li className="flex items-start gap-2 text-[10px] font-mono text-white/50">
            <div className="w-1 h-1 rounded-full bg-lattice-cyan mt-1" />
            <span>Deploy a <span className="text-white">Sovereign Gateway</span> on a router running OpenWRT or DD-WRT.</span>
          </li>
          <li className="flex items-start gap-2 text-[10px] font-mono text-white/50">
            <div className="w-1 h-1 rounded-full bg-lattice-cyan mt-1" />
            <span>Bridge your local router to the <span className="text-white">Absolute Zero Node</span> via Ethernet or Wireless Bridge.</span>
          </li>
          <li className="flex items-start gap-2 text-[10px] font-mono text-white/50">
            <div className="w-1 h-1 rounded-full bg-lattice-cyan mt-1" />
            <span>Disable DNS Rebind protection so the router can masquerade the lattice probes.</span>
          </li>
        </ul>
        <div className="bg-black/20 p-3 rounded border border-white/5 font-mono text-[9px] text-lattice-cyan/60">
          Tip: You can find "Bridge Mode" in almost all legacy routers. This allows the hardware to extend the Lattice signal throughout your physical space indefinitely.
        </div>
      </div>
    </div>
  );
};
